import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Lock, Shield, CheckCircle } from 'lucide-react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const FirstAccess: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { firstAccessUser, setFirstAccessUser } = useAuth();

  // Verificar se há usuário de primeiro acesso
  useEffect(() => {
    if (!firstAccessUser) {
      // Se não há usuário de primeiro acesso, redirecionar para login
      navigate('/login');
    }
  }, [firstAccessUser, navigate]);

  // Se não há usuário, não renderizar nada
  if (!firstAccessUser) {
    return null;
  }

  // Verificar se o usuário tem todas as propriedades necessárias
  if (!firstAccessUser.name || !firstAccessUser.email || !firstAccessUser.id) {
    console.error('❌ [FirstAccess] Dados do usuário incompletos:', firstAccessUser);
    navigate('/login');
    return null;
  }

  const user = firstAccessUser;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Todos os campos são obrigatórios.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: 'Senha muito curta',
        description: 'A nova senha deve ter no mínimo 6 caracteres.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Senhas não coincidem',
        description: 'As senhas digitadas não são iguais.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/change-first-access-password', {
        userId: user.id,
        currentPassword,
        newPassword,
      });

      setIsSuccess(true);
      toast({
        title: 'Senha alterada com sucesso!',
        description: 'Agora você pode fazer login normalmente.',
      });

      // Limpar usuário de primeiro acesso
      setFirstAccessUser(null);

      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Erro ao alterar senha. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-gray-900">Senha Alterada!</h3>
              <p className="text-gray-600">
                Sua senha foi alterada com sucesso, {user.name}.
              </p>
              <p className="text-sm text-gray-500">
                Redirecionando para a página de login...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-montserrat">Primeiro Acesso</CardTitle>
          <CardDescription>
            Olá, <strong>{user.name}</strong>! Você precisa alterar sua senha temporária.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Lock className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">
                  Senha Temporária Recebida
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Use a senha temporária que foi enviada para <strong>{user.email}</strong> para continuar.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="font-medium">
                Senha Temporária
              </Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Digite a senha enviada por e-mail"
                required
                disabled={isLoading}
                className="border-gray-200 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500">
                A senha temporária foi enviada para seu e-mail
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="font-medium">
                Nova Senha
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                disabled={isLoading}
                className="border-gray-200 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500">
                A senha deve ter no mínimo 6 caracteres
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-medium">
                Confirmar Nova Senha
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                disabled={isLoading}
                className="border-gray-200 focus:border-blue-500"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Alterando...
                </>
              ) : (
                'Alterar Senha'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/login')}
              disabled={isLoading}
              className="w-full"
            >
              Voltar para Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FirstAccess;
